import { Component, Input, Output, EventEmitter, inject, OnDestroy, computed, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Skeleton } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ThemeService } from '../../core/services/theme.service';
import { AuthService } from '../../core/services/auth.service';
import { MenuService } from '../../core/services/menu.service';
import { Organization } from '../../core/models/auth.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, RouterModule, Button, Dialog, Skeleton, ToastModule],
  providers: [MessageService],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.css',
  host: {
    'class': 'h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-3 md:px-5 flex items-center justify-between z-20 shadow-2xs shrink-0'
  }
})
export class TopbarComponent implements OnDestroy {
  readonly isTestEnvironment = !environment.production;
  readonly apiUrl = environment.apiUrl;
  private readonly router = inject(Router);
  protected readonly themeService = inject(ThemeService);
  private readonly authService = inject(AuthService);
  private readonly menuService = inject(MenuService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly currentUser = this.authService.currentUser;

  protected readonly userInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return 'AD';
    if (user.fullName) {
      const parts = user.fullName.trim().split(/\s+/);
      if (parts.length > 1) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return user.fullName.substring(0, 2).toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  });

  protected readonly userFullName = computed(() => {
    return this.currentUser()?.fullName || 'Admin Demo';
  });

  protected readonly userRoleName = computed(() => {
    const role = this.currentUser()?.role;
    if (!role) return 'System Admin';
    return role.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  });

  @Input() isSidebarCollapsed = false;
  @Input() mobileOpen = false;

  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() toggleMobile = new EventEmitter<void>();

  // Profile dropdown open state
  dropdownOpen = false;
  private hoverTimeout: any;

  // Organization Switching State
  displaySwitchOrgModal = false;
  readonly loadingOrganizations = signal<boolean>(false);
  readonly userOrganizations = signal<Organization[]>([]);
  readonly selectedOrg = signal<Organization | null>(null);
  readonly switchingOrg = signal<boolean>(false);

  showDropdown(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    this.dropdownOpen = true;
  }

  hideDropdown(): void {
    this.dropdownOpen = false;
  }

  openSwitchOrgModal(): void {
    this.dropdownOpen = false;
    this.displaySwitchOrgModal = true;
    this.loadingOrganizations.set(true);

    const activeOrgId = this.currentUser()?.activeOrganizationId || this.currentUser()?.organizationId;
    const activeOrgCode = this.currentUser()?.activeOrganizationCode;

    this.authService.getUserOrganizations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orgs) => {
          this.userOrganizations.set(orgs);
          if (orgs.length > 0) {
            const currentOrSelected = orgs.find((o) => (activeOrgCode && o.code === activeOrgCode) || (activeOrgId && Number(o.id) === Number(activeOrgId))) ||
                                    orgs.find((o) => o.isDefault) ||
                                    orgs[0];
            this.selectedOrg.set(currentOrSelected);
          } else {
            this.selectedOrg.set(null);
          }
          this.loadingOrganizations.set(false);
        },
        error: () => {
          this.loadingOrganizations.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load user organizations.'
          });
        }
      });
  }

  selectOrg(org: Organization): void {
    this.selectedOrg.set(org);
  }

  confirmSwitchOrganization(): void {
    const target = this.selectedOrg();
    if (!target) return;

    this.switchingOrg.set(true);

    const orgCode = target.code || String(target.id);
    this.authService.switchOrganization(orgCode, target.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.switchingOrg.set(false);
          this.displaySwitchOrgModal = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Organization Switched',
            detail: `Active organization changed to ${target.name}`
          });
          // Refresh navigation menu tree for new organization permissions
          this.menuService.loadUserMenus().subscribe();
        },
        error: (err) => {
          this.switchingOrg.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Switch Failed',
            detail: err.error?.message || 'Failed to switch organization.'
          });
        }
      });
  }

  logout(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  }
}
